import type { HierarchicalLogger } from '@atlassian/logger-interface';
import type { TapProvider } from '@post-office/shared-contracts/tap-provider';
import { getTapTraitsForSite } from '@post-office/util-tap-traits';

import {
	type ConfSamplePageLocation,
	parseTwcTriggerConfTourTrait,
} from './parse-twc-trigger-conf-tour-trait';

export const TWC_TRIGGER_CONF_TOUR_TRAIT = 'twc-trigger-conf-tour';

/**
 * Fetch and parse the twc-trigger-conf-tour TAP trait for a site and return the Confluence sample page location.
 *
 * @param tenantId - The tenant ID (site ID) to fetch traits for
 * @param tapProvider - The TAP provider instance
 * @param logger - Optional logger for error logging
 * @returns Parsed location with spaceId and pageId, or null if not found/invalid
 */
export async function getConfSamplePageLocation({
	tenantId,
	tapProvider,
	logger,
}: {
	tenantId: string;
	tapProvider: TapProvider;
	logger?: HierarchicalLogger;
}): Promise<ConfSamplePageLocation | null> {
	try {
		const siteTapTraits = await getTapTraitsForSite({
			siteId: tenantId,
			tapProvider,
			logger,
		});

		const traitValue = siteTapTraits?.[TWC_TRIGGER_CONF_TOUR_TRAIT];

		// Trait value should be a string containing ARIs
		if (typeof traitValue !== 'string') {
			return null;
		}

		return parseTwcTriggerConfTourTrait(traitValue);
	} catch (error) {
		logger?.error(
			{
				tenantId,
				error: error instanceof Error ? error.message : 'Unknown error',
			},
			'Error fetching twc-trigger-conf-tour TAP trait',
		);
		return null;
	}
}
